import { useEffect, useState } from "react";
import { Box, CircularProgress, Grid2 } from "@mui/material";
import {
  getReclasificationReport,
  updateReclasificationReport,
} from "../../../../apis";
import { InputCatalogue } from "../../../MainComponents/Catalogues/InputCatalogue";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import { ReportHeader2 } from "../../../Title/ReportHeader2";
import { useTranslation } from "react-i18next";
import { IconHeader } from "../../../Icon/IconHader/IconHeader";
import { Loading } from "../../../Loading/String/Loading";
import { ReportHeader } from "../../../Title/ReportHeader";
import { enqueueSnackbar } from "notistack";

export const ReclassificationComponent = ({
  reportDetail,
  setReclassificationReport,
  reloadReport,
}) => {
  const id = reportDetail?.id;
  const [t] = useTranslation("report");

  const [fields, setFields] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [newValues, setNewValues] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!id) return;

    let isMounted = true;

    const fetchData = async () => {
      try {
        const data = await getReclasificationReport(id);
        if (!isMounted) return;

        setFields(data.fields ?? []);
      } catch (error) {
        console.error("Error fetching reclasification report", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleChangeNewValues = (key, value) => {
    setNewValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Update values on DB
  const updateValues = async () => {
    const keys = Object.keys(newValues);
    if (keys.length === 0) return;
    try {
      setIsUpdating(true);
      const payload = keys.map((k) => ({ key: k, value: newValues[k] }));

      const resp = await updateReclasificationReport(id, { fields: payload });
      setFields(resp.fields ?? []);
      setNewValues({});
      reloadReport();
      enqueueSnackbar("Reporte actualizado", { variant: "success" });
    } catch (error) {
      console.error("Error updating values:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return isLoading ? (
    <Loading />
  ) : (
    fields.length > 0 && (
      <Box>
        <ReportHeader
          reportDetail={reportDetail}
          edit
          title={t("ReportViewer.report_reclassification")}
          icon="outline_edit"
        />
        <ReportHeader2
          reportDetail={reportDetail}
          showAssignedUsers={false}
          showIndicators={false}
          showIsAnonymous={false}
          showTrackingCode={false}
          buttons={[
            {
              tooltip: t("ReportViewer.exit"),
              type: "icon-button",
              icon: "bold_logout",
              onClick: () => setReclassificationReport(false),
            },
          ]}
        />

        <Box className="dyTheme1 dyBorder1 rounded" p={3} mt={2}>
          {fields.map((field) => (
            <Box key={field.key} mb={0.5}>
              <IconHeader title={field.label} uppercase={false} />
              <Grid2 container spacing={2} p={1}>
                <ReclassificationField
                  field={field}
                  handleChangeNewValues={handleChangeNewValues}
                />
              </Grid2>
            </Box>
          ))}
          <EGButton
            label={t("ReportViewer.update")}
            onClick={updateValues}
            loading={isUpdating}
            sx={{ mt: 2 }}
            color="success"
            labelColor="white"
            iconName="mark_check_bold"
            iconColor="white"
            iconColorHover="white"
          />
        </Box>
      </Box>
    )
  );
};

const ReclassificationField = ({ field, handleChangeNewValues }) => {
  const isOwn = field.key.split("::")[1] === "OWN";
  const { catalogue, value: defaultValue, label } = field;

  const onChange = (value) => {
    handleChangeNewValues(field.key, value);
  };

  if (!catalogue) return null;

  return (
    <InputCatalogue
      label={label}
      catalogue={catalogue}
      defaultValue={defaultValue}
      isOwn={isOwn}
      onChange={onChange}
      isValid={true}
      wrapper={<Grid2 size={{ xs: 12, sm: 6, md: 4 }} />}
    />
  );
};
